import { getCurrentReportDateFilters } from '@/lib/report-date-range'
import type { ConfigType } from 'dayjs'
import { z } from 'zod/v4'
import type {
  PlatformLedgerAccount,
  PlatformLedgerBasis,
  PlatformLedgerSort,
  PlatformLedgerView,
} from './platform-ledger.types'

export const PLATFORM_LEDGER_BASIS = {
  CASH: 'cash',
  OWED: 'owed',
} as const satisfies Record<string, PlatformLedgerBasis>

export const PLATFORM_LEDGER_VIEW = {
  CASH: 'cash',
  FEES_OWED: 'fees_owed',
} as const satisfies Record<string, PlatformLedgerView>

export const PLATFORM_LEDGER_ACCOUNT = {
  PLATFORM_FEE_REVENUE: 'platform_fee_revenue',
  POS_FEE_REVENUE: 'pos_fee_revenue',
  MERCHANT_FEE_REVENUE: 'merchant_fee_revenue',
  PROCESSOR_COST: 'processor_cost',
} as const satisfies Record<string, PlatformLedgerAccount>

export const PLATFORM_LEDGER_SORT = {
  RECOGNIZED_DESC: 'recognized_desc',
  RECOGNIZED_ASC: 'recognized_asc',
  AMOUNT_DESC: 'amount_desc',
  AMOUNT_ASC: 'amount_asc',
  ACCOUNT_ASC: 'account_asc',
  ACCOUNT_DESC: 'account_desc',
  FLORIST_ASC: 'florist_asc',
  FLORIST_DESC: 'florist_desc',
} as const satisfies Record<string, PlatformLedgerSort>

export const PLATFORM_LEDGER_PAGE_SIZE = 20

export const PLATFORM_LEDGER_VIEW_OPTIONS = [
  { value: PLATFORM_LEDGER_VIEW.CASH, label: 'Cash' },
  { value: PLATFORM_LEDGER_VIEW.FEES_OWED, label: 'Fees still owed' },
]

export const PLATFORM_LEDGER_ACCOUNT_OPTIONS = [
  { value: PLATFORM_LEDGER_ACCOUNT.PLATFORM_FEE_REVENUE, label: 'Platform fee revenue' },
  { value: PLATFORM_LEDGER_ACCOUNT.POS_FEE_REVENUE, label: 'POS fee revenue' },
  { value: PLATFORM_LEDGER_ACCOUNT.MERCHANT_FEE_REVENUE, label: 'Merchant fee revenue' },
  { value: PLATFORM_LEDGER_ACCOUNT.PROCESSOR_COST, label: 'Processing Costs' },
]

const dateStringSchema = z.iso.date('Invalid date')
const viewSchema = z.enum(Object.values(PLATFORM_LEDGER_VIEW))
const accountSchema = z.enum(Object.values(PLATFORM_LEDGER_ACCOUNT))
const sortSchema = z.enum(Object.values(PLATFORM_LEDGER_SORT))
const dateRangeError = {
  message: 'Start date must be on or before end date',
  path: ['end_date'],
}

interface OptionalDateRange {
  start_date?: string | undefined
  end_date?: string | undefined
}

function hasCompleteDateRange({ start_date, end_date }: OptionalDateRange) {
  if (!start_date && !end_date) {
    return true
  }

  if (!start_date || !end_date) {
    return false
  }

  return start_date <= end_date
}

export const platformLedgerReportSearchSchema = z
  .object({
    start_date: dateStringSchema.optional(),
    end_date: dateStringSchema.optional(),
    // A bookmark from before the projected views reads the cash view.
    view: viewSchema.optional().catch(undefined),
    account: accountSchema.optional(),
    search: z.string().optional().catch(undefined),
    sort: sortSchema.optional(),
    page: z.coerce.number().int().positive().optional(),
    page_size: z.coerce.number().int().positive().max(100).optional(),
  })
  .refine(hasCompleteDateRange, dateRangeError)

export type PlatformLedgerReportSearch = z.infer<typeof platformLedgerReportSearchSchema>

/** The filters the platform ledger page requests (`PlatformLedgerEntriesRequest`). */
export interface PlatformLedgerReportFilters {
  start_date: string
  end_date: string
  view: PlatformLedgerView
  account: PlatformLedgerAccount | null
  search: string | null
  sort: PlatformLedgerSort
  page: number
  page_size: number
}

export function getDefaultPlatformLedgerReportFilters(
  now: ConfigType
): PlatformLedgerReportFilters {
  return {
    ...getCurrentReportDateFilters(now),
    view: PLATFORM_LEDGER_VIEW.CASH,
    account: null,
    search: null,
    sort: PLATFORM_LEDGER_SORT.RECOGNIZED_DESC,
    page: 1,
    page_size: PLATFORM_LEDGER_PAGE_SIZE,
  }
}

export function getPlatformLedgerReportFiltersFromSearch(
  search: unknown,
  defaultFilters: PlatformLedgerReportFilters
): PlatformLedgerReportFilters {
  const parsedSearch = platformLedgerReportSearchSchema.parse(search)
  const trimmedSearch = parsedSearch.search?.trim()
  const { start_date: startDate, end_date: endDate } = parsedSearch
  const explicitRange = startDate && endDate ? { startDate, endDate } : null
  return {
    start_date: explicitRange ? explicitRange.startDate : defaultFilters.start_date,
    end_date: explicitRange ? explicitRange.endDate : defaultFilters.end_date,
    view: parsedSearch.view ?? defaultFilters.view,
    account: parsedSearch.account ?? null,
    search: trimmedSearch || null,
    sort: parsedSearch.sort ?? defaultFilters.sort,
    page: parsedSearch.page ?? defaultFilters.page,
    page_size: parsedSearch.page_size ?? defaultFilters.page_size,
  }
}

export function getPlatformLedgerReportSearchParams(filters: PlatformLedgerReportFilters) {
  return {
    start_date: filters.start_date,
    end_date: filters.end_date,
    view: filters.view && filters.view !== PLATFORM_LEDGER_VIEW.CASH ? filters.view : undefined,
    account: filters.account || undefined,
    search: filters.search || undefined,
    sort:
      filters.sort && filters.sort !== PLATFORM_LEDGER_SORT.RECOGNIZED_DESC
        ? filters.sort
        : undefined,
    page: filters.page && filters.page !== 1 ? filters.page : undefined,
    page_size:
      filters.page_size && filters.page_size !== PLATFORM_LEDGER_PAGE_SIZE
        ? filters.page_size
        : undefined,
  }
}
